/**
 * ErrorBoundary — friendly runtime crash fallback.
 *
 * Catches uncaught render errors anywhere below it and shows a calm, non-technical
 * fallback instead of a raw crash/white screen. The full stack trace is NEVER shown
 * to normal users — it is only sent (on the user's explicit click) as a support
 * "error_report" ticket together with route + app version/build + device context.
 * User/team identity is attached server-side from the auth token.
 */
import React from 'react';
import { sendErrorReport } from '../lib/api';
import { APP_VERSION, BUILD_ID } from '../lib/version';

function getDeviceType() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobile|iPhone|Android|iPod|IEMobile|BlackBerry/i.test(ua)) return 'mobile';
  return 'desktop';
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, stack: '', sending: false, sent: false, failed: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Capture stack for the (optional) error report — never rendered to the user.
    this.setState({ stack: (info && info.componentStack) ? info.componentStack : (error?.stack || '') });
    console.error('Uncaught UI error:', error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, stack: '', sent: false, failed: false });
  };

  handleSendReport = async () => {
    this.setState({ sending: true, failed: false });
    try {
      const msg = this.state.error?.message || String(this.state.error || 'Unknown error');
      await sendErrorReport({
        title: `Felrapport: ${msg.slice(0, 90)}`,
        description: 'Automatiskt insamlad felrapport från appen (skickad av användaren).',
        error_message: msg.slice(0, 1000),
        stack_trace: ((this.state.error?.stack || '') + '\n' + (this.state.stack || '')).slice(0, 6000),
        app_version: APP_VERSION,
        build_id: BUILD_ID,
        platform: getDeviceType() === 'desktop' ? 'desktop-web' : 'mobile-web',
        device_type: getDeviceType(),
        device_info: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 500),
        current_route: typeof window !== 'undefined' ? window.location.pathname : '',
      });
      this.setState({ sending: false, sent: true });
    } catch (e) {
      this.setState({ sending: false, failed: true });
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: '100dvh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 'max(24px, env(safe-area-inset-top)) 24px max(24px, env(safe-area-inset-bottom))',
          background: "rgb(var(--tn-bg-0e1116))", color: "var(--tn-readable-e7e9ee, rgb(var(--tn-text-e7e9ee)))",
        }}
      >
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: '0 auto 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(201,166,70,0.15)', color: "var(--tn-readable-c9a646, rgb(var(--tn-text-c9a646)))", fontSize: 28,
          }}>!</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Något gick fel.</h1>
          <p style={{ color: "var(--tn-readable-9aa0aa, rgb(var(--tn-text-9aa0aa)))", fontSize: 15, margin: '0 0 24px', lineHeight: 1.5 }}>
            Du kan försöka igen eller skicka en felrapport så vi kan fixa det.
          </p>

          {this.state.sent ? (
            <p data-testid="error-report-sent" style={{ color: "var(--tn-readable-34d399, rgb(var(--tn-text-34d399)))", fontSize: 14, marginBottom: 16 }}>
              Tack! Felrapporten är skickad.
            </p>
          ) : null}
          {this.state.failed ? (
            <p style={{ color: "var(--tn-readable-f87171, rgb(var(--tn-text-f87171)))", fontSize: 13, marginBottom: 16 }}>
              Kunde inte skicka felrapporten just nu.
            </p>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              data-testid="error-retry-btn"
              onClick={this.handleRetry}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12, border: 'none',
                background: '#c9a646', color: '#1a1a1a', fontWeight: 600, fontSize: 15, cursor: 'pointer',
              }}
            >
              Försök igen
            </button>
            {!this.state.sent && (
              <button
                data-testid="error-send-report-btn"
                onClick={this.handleSendReport}
                disabled={this.state.sending}
                style={{
                  width: '100%', padding: '12px 16px', borderRadius: 12,
                  border: "1px solid rgb(var(--tn-border-2a2f3a))", background: 'transparent', color: "var(--tn-readable-e7e9ee, rgb(var(--tn-text-e7e9ee)))",
                  fontWeight: 600, fontSize: 15, cursor: this.state.sending ? 'wait' : 'pointer',
                  opacity: this.state.sending ? 0.6 : 1,
                }}
              >
                {this.state.sending ? 'Skickar…' : 'Skicka felrapport'}
              </button>
            )}
            <button
              data-testid="error-reload-btn"
              onClick={() => window.location.reload()}
              style={{
                width: '100%', padding: '10px 16px', borderRadius: 12, border: 'none',
                background: 'transparent', color: "var(--tn-readable-9aa0aa, rgb(var(--tn-text-9aa0aa)))", fontSize: 14, cursor: 'pointer',
              }}
            >
              Ladda om appen
            </button>
          </div>
        </div>
      </div>
    );
  }
}
